import React from 'react'
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Snackbar,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material'
import { IStockCard, StockCard } from 'interfaces/IStockCard'
import { IReduxState } from 'interfaces/IReduxState'
import { useSelector } from 'react-redux'
import API from '@aws-amplify/api'
import {
  STOCK_CARD_TYPE_INGREDIENTS,
  VALUE_EXPIRED,
  VALUE_FAIL_QC,
  VALUE_GUDANG_MIXING,
  VALUE_IN,
  VALUE_OUT,
  VALUE_RETURN_TO_SUPPLIER,
  VALUE_RND,
} from 'enums/stockCard'
import MuiAlert from '@mui/material/Alert'
import LoadIngredientAutocomplete from 'shared/autocomplete/loadIngredientAutocomplete'
import { IIngredient } from 'interfaces/IIngredient'

export default function OutgoingIngredientStockCard(props: {}) {
  const user = useSelector((state: IReduxState) => state.currentUser || {})

  const [stockCardData, setStockCardData] = React.useState<IStockCard>(
    new StockCard({
      inOutDirection: VALUE_IN,
      type: STOCK_CARD_TYPE_INGREDIENTS,
    })
  )
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [ingredientData, setIngredientData] =
    React.useState<IIngredient | null>(null)

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const getValidationErrors = () => {
    const errors: string[] = []
    if (!stockCardData.ingredientId) errors.push('Bahan Baku wajib dipilih')
    if (!stockCardData.outgoingDestination) errors.push('Tujuan Keluar wajib dipilih')
    if (!stockCardData.quantity || Number(stockCardData.quantity) <= 0) errors.push('Quantity Keluar harus > 0')

    return errors
  }

  const isAllFieldsComplete = () => {
    return getValidationErrors().length === 0
  }

  const saveStockCard = () => {
    setIsLoading(true)

    API.post('products', `/premix/createStockCard`, {
      body: {
        ...stockCardData,
        ingredientId: ingredientData?.ingredientId,
        type: STOCK_CARD_TYPE_INGREDIENTS,
        inOutDirection: VALUE_OUT,
        isConfirmed: true,
      },
    })
      .then((response) => {
        setAlertMessage('Berhasil membuat kartu stok.')
        setIsSuccessSnackbarOpen(true)
        setStockCardData(new StockCard())
        window.location.reload()
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage('Gagal membuat kartu stok.')
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleStockCardFieldChange = (value: any, key: string): void => {
    setStockCardData((stockCardData) => ({ ...stockCardData, [key]: value }))
  }

  return (
    <React.Fragment>
      <Paper>
        <React.Fragment>
          <List>
            <ListItem>
              <LoadIngredientAutocomplete
                onChange={(e) => {
                  setIngredientData(e)
                  handleStockCardFieldChange(e.ingredientId, 'ingredientId')
                }}
              />
            </ListItem>
            <ListItem>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  Barang Keluar Untuk Departemen Mana
                </FormLabel>
                <RadioGroup
                  id="filter-status-dropdown"
                  row
                  aria-label="position"
                  value={stockCardData.outgoingDestination}
                  onChange={(e) =>
                    handleStockCardFieldChange(
                      e.target.value,
                      'outgoingDestination'
                    )
                  }
                >
                  <FormControlLabel
                    value={VALUE_GUDANG_MIXING}
                    control={<Radio color="primary" />}
                    label={'Gudang Mixing'}
                  />
                  <FormControlLabel
                    value={VALUE_RND}
                    control={<Radio color="primary" />}
                    label={'RnD'}
                  />
                  <FormControlLabel
                    value={VALUE_RETURN_TO_SUPPLIER}
                    control={<Radio color="primary" />}
                    label={'Kembali ke Supplier'}
                  />
                  <FormControlLabel
                    value={VALUE_EXPIRED}
                    control={<Radio color="primary" />}
                    label={'Expired'}
                  />
                  <FormControlLabel
                    value={VALUE_FAIL_QC}
                    control={<Radio color="primary" />}
                    label={'Instruksi QC / Accounting'}
                  />
                </RadioGroup>
              </FormControl>
            </ListItem>
            <ListItem>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={stockCardData.quantity}
                fullWidth
                type="number"
                inputProps={{ step: '0.000001' }}
                label="Quantity (kg)"
                onChange={(e) =>
                  handleStockCardFieldChange(e.target.value, 'quantity')
                }
              />
            </ListItem>
            <ListItem>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={stockCardData.notes}
                fullWidth
                label={'Notes'}
                onChange={(e) =>
                  handleStockCardFieldChange(e.target.value, 'notes')
                }
              />
            </ListItem>
            <ListItem>
              <Typography>Diterima Oleh: {user.username}</Typography>
            </ListItem>
          </List>
          <Box mt={3} />
          <Paper>
            <Box p={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                disabled={!isAllFieldsComplete() || isLoading}
                onClick={saveStockCard}
              >
                Save
              </Button>
              {!isAllFieldsComplete() && (
                <Box mt={1}>
                  {getValidationErrors().map((err) => (
                    <Typography key={err} variant="caption" color="error" display="block">
                      • {err}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </React.Fragment>{' '}
      </Paper>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  )
}
